import * as Joi from 'joi';
import {CandidateProfileFormData} from '../../../models';
import {
  getEmailSchemaPair,
  getJoiObject,
  getMasterCodeObjectDropdownSchema,
  getPhoneNumberSchema,
  validateFloor,
  validateHouseBlockNo,
  validatePostalCode,
  validateSingaporeCountry,
  validateStreetName,
  validateUnit,
} from '../../../utils/JoiUtils';

const countryCodeKey = 'Country Code';
const telephoneKey = 'Telephone No.';
const resitEditCandidateProfileSchema = () =>
    Joi.object<CandidateProfileFormData>({
      email: getJoiObject(getEmailSchemaPair('Email Address')),
      houseOrBlkNo: getJoiObject(
          Joi.string()
          .max(10)
          .required()
          .when('postalCode', {
            is: Joi.string().not('000000'),
            then: Joi.custom(validateHouseBlockNo),
          })
          .messages({
            'string.empty': 'House/Block No. is not allowed to be empty.',
            'string.max': 'House/Block No. is too long.',
            'string.pattern.houseBlockNo': 'First character must be number.',
          })
      ),
      streetName: getJoiObject(
          Joi.string().max(32).required().custom(validateStreetName).messages({
            'string.empty': 'Street Name is not allowed to be empty.',
            'string.max': 'Street Name is too long.',
            'string.pattern.streetName': 'First character must be alphabet letter.',
          })
      ),
      building: getJoiObject(
          Joi.string().max(66).allow('', null).messages({
            'string.max': 'Building is too long.',
          })
      ),
      floor: getJoiObject(
          Joi.string().max(2).allow('', null).custom(validateFloor).messages({
            'string.max': 'Floor is too long.',
            'string.pattern.floor': 'Please enter number.',
          })
      ),
      unit: getJoiObject(
          Joi.string().max(5).allow('', null).custom(validateUnit).messages({
            'string.max': 'Unit is too long.',
            'string.pattern.unit': 'Please enter number and/or alphabet letter.',
          })
      ),
      postalCode: getJoiObject(
          Joi.string().max(6).required().custom(validatePostalCode).messages({
            'string.empty': 'Postal Code is not allowed to be empty.',
            'string.max': 'Postal Code is too long.',
            'string.pattern.postalCode': 'Please enter 6 digits.',
          })
      ),
      foreignAddress1: getJoiObject(
          Joi.when('preferredAddress', {
            is: 'F',
            then: Joi.string().required().max(60).messages({
              'string.empty':
                  'Foreign Address 1 is not allowed to be empty when foreign address is the preferred address.',
              'string.max': 'Foreign Address 1 is too long.',
            }),
            otherwise: Joi.string().allow('', null).max(60).messages({
              'string.max': 'Foreign Address 1 is too long.',
            }),
          })
      ),
      foreignAddress2: getJoiObject(
          Joi.string().allow('', null).max(60).messages({
            'string.max': 'Foreign Address 2 is too long.',
          })
      ),
      foreignAddress3: getJoiObject(
          Joi.string().allow('', null).max(60).messages({
            'string.max': 'Foreign Address 3 is too long.',
          })
      ),
      telephoneCountryCode: getJoiObject(
          Joi.when('preferredTelephoneOrMobile', {
            is: 'T',
            then: getMasterCodeObjectDropdownSchema(false, countryCodeKey),
            otherwise: Joi.when('telephoneContactNumber', {
              is: Joi.string().exist(),
              then: getMasterCodeObjectDropdownSchema(false, countryCodeKey),
              otherwise: getMasterCodeObjectDropdownSchema(true, countryCodeKey),
            }),
          })
      ),
      telephoneContactNumber: getJoiObject(
          Joi.when('telephoneCountryCode', {
            is: validateSingaporeCountry,
            then: Joi.when('preferredTelephoneOrMobile', {
              is: 'T',
              then: getPhoneNumberSchema(false, telephoneKey, 8, 8),
              otherwise: getPhoneNumberSchema(true, telephoneKey, 8, 8),
            }),
            otherwise: Joi.when('preferredTelephoneOrMobile', {
              is: 'T',
              then: getPhoneNumberSchema(false, telephoneKey),
              otherwise: getPhoneNumberSchema(true, telephoneKey),
            }),
          })
      ),
      mobileCountryCode: getJoiObject(getMasterCodeObjectDropdownSchema(false, countryCodeKey)),
      mobileContactNumber: getJoiObject(
          Joi.when('mobileCountryCode', {
            is: validateSingaporeCountry,
            then: getPhoneNumberSchema(false, 'Mobile No.', 8, 8),
            otherwise: getPhoneNumberSchema(false, 'Mobile No.'),
          })
      ),
    });

export default resitEditCandidateProfileSchema;
